/* ------------------------------------------------------------------ *
 * 一、数据授权（四级 + 匿名科研）
 * ------------------------------------------------------------------ */
export const consentScopes = [
    {
        key: 'physio',
        label: '生理医疗数据',
        desc: '体检报告、检验指标、影像与病历摘要',
        level: 1,
    },
    {
        key: 'behavior',
        label: '行为习惯数据',
        desc: '睡眠、运动、压力、用药与饮食记录',
        level: 2,
    },
    {
        key: 'enterprise',
        label: '企业经营数据',
        desc: '企业健康度评估、组织与财务口径数据',
        level: 3,
    },
    {
        key: 'wealth',
        label: '资产与保障数据',
        desc: '资产分布、保险保障与传承安排',
        level: 4,
    },
    {
        key: 'research',
        label: '匿名化科研贡献',
        desc: '去标识后参与联合建模，可随时撤回',
        level: 5,
    },
];
/* ------------------------------------------------------------------ *
 * 二、三级预警
 *
 * 注意：这里的键就是接口返回值本身（'info' | 'warn' | 'danger'）。
 * 早先的版本把键写成 L1/L2/L3、把值写成 info/warn/danger，导致
 * `alert.level === 'danger'` 这类判断在类型层面被判为「不可能成立」——
 * 契约必须与接口实际返回值一字不差。
 * ------------------------------------------------------------------ */
export const alertLevels = {
    info: { label: '一级 · 提示', color: 'info', order: 1 },
    warn: { label: '二级 · 关注', color: 'warn', order: 2 },
    danger: { label: '三级 · 预警', color: 'danger', order: 3 },
};
/* ------------------------------------------------------------------ *
 * 三、H-Score™ 五维
 * ------------------------------------------------------------------ */
export const healthDimensions = [
    { key: 'bio', label: '生物年龄', weight: 0.25 },
    { key: 'organ', label: '器官功能', weight: 0.22 },
    { key: 'chronic', label: '慢病风险', weight: 0.23 },
    { key: 'stress', label: '心理压力', weight: 0.15 },
    { key: 'recover', label: '恢复能力', weight: 0.15 },
];
/**
 * 生命健康的多模态指标分层 —— **新版校正为 6 层**（旧版为生理/行为/环境/经营 4 层）。
 *
 * 口径依据：新版原型 `data.js` 的 `h.layers` 给出 6 个层名与各层包含的项目描述；
 * **源材料没有给出每层的项数**（187 项 / 26 维度是横跨 H/E/W 的平台总数，
 * 见 `caliber.ts` 的 `skCaliber.scale`）。因此这里只保留名称与描述，不编造数字。
 */
export const indicatorLayers = [
    {
        key: 'molecular',
        name: '细胞与分子层',
        color: 'h',
        desc: 'TH 超微细胞动态监测 · 12 万倍目镜电镜 · 端粒长度 · 表观遗传时钟',
    },
    {
        key: 'organ',
        name: '器官功能层',
        color: 'h',
        desc: 'HRA 健康风险评估 · TTM 热断层扫描 · 脏器功能与结构影像',
    },
    {
        key: 'metabolic',
        name: '循环与代谢层',
        color: 'e',
        desc: '血脂亚组分 · 血糖稳态 · 同型半胱氨酸 · 尿酸与肝肾功能',
    },
    {
        key: 'immune',
        name: '免疫与炎症层',
        color: 'e',
        desc: '免疫细胞亚群 · 慢性炎症因子 · 自身抗体谱 · 肿瘤标志物',
    },
    {
        key: 'neuro',
        name: '神经与心理层',
        color: 'w',
        desc: '认知功能评估 · 压力与情绪量表 · HRV 自主神经平衡',
    },
    {
        key: 'sleep',
        name: '行为与睡眠层',
        color: 'w',
        desc: '睡眠结构 · 运动与依从性 · 饮食结构 · 职业与生活节律',
    },
];
/* ------------------------------------------------------------------ *
 * 四、E-Health Index 五维（一级 5 项 + 二级 19 项）
 *
 * 口径说明：产品设计说明与原型注释早期写作「18 项」，但原型 data.js 实际定义了
 * 4+4+4+4+3 = 19 项。经确认，**权威口径为 19 项**（详见 docs/架构与实现说明.md 第八节）。
 * 界面一律按实际数据长度渲染，不写死数量。
 * ------------------------------------------------------------------ */
export const enterpriseDimensions = [
    { key: 'leadership', name: '领导力健康', weight: 30 },
    { key: 'resilience', name: '组织韧性', weight: 25 },
    { key: 'culture', name: '健康文化', weight: 20 },
    { key: 'risk', name: '风险管控', weight: 15 },
    { key: 'value', name: '价值创造', weight: 10 },
];
/* ------------------------------------------------------------------ *
 * 五、健康信托三档
 * ------------------------------------------------------------------ */
export const trustPlans = [
    {
        key: 'basic',
        name: '基础型',
        priceFrom: '500 万起',
        summary: '年度体检 · 健康顾问 · 三甲绿通',
        order: 1,
    },
    {
        key: 'pro',
        name: '进阶型',
        priceFrom: '2,000 万起',
        summary: '深度检测 · MDT 会诊 · 专项疗养',
        order: 2,
    },
    {
        key: 'elite',
        name: '尊享型',
        priceFrom: '5,000 万起',
        summary: '跨境医疗 · 两代健康信托 · 家族健康宪章',
        order: 3,
    },
];
/* ------------------------------------------------------------------ *
 * 六、传承学院：三层五维
 * ------------------------------------------------------------------ */
export const successionLayers = [
    { key: 'bedrock', name: '基石养成层', sub: '培养「完整的人」', goal: '卓越心智 · 健全人格 · 全球视野与社会责任感', color: 'h', order: 1 },
    { key: 'forge', name: '能力锻造层', sub: '锤炼「将领」', goal: '商业智慧 · 组织驾驭力 · 创新攻坚力', color: 'e', order: 2 },
    { key: 'strategy', name: '战略目标层', sub: '塑造「舵手」', goal: '价值领导力 · 战略思维 · 事业共同体认同', color: 'w', order: 3 },
];
export const successionDimensions = [
    { key: 'cognition', name: '认知维', order: 1 },
    { key: 'character', name: '品格维', order: 2 },
    { key: 'ability', name: '能力维', order: 3 },
    { key: 'experience', name: '经验维', order: 4 },
    { key: 'network', name: '网络维', order: 5 },
];
/* ------------------------------------------------------------------ *
 * 七、成员等级
 * ------------------------------------------------------------------ */
export const memberLevels = [
    { key: 'founding', name: '创始共建成员', en: 'FOUNDING PARTNER', order: 1 },
    { key: 'core', name: '核心共建成员', en: 'CORE PARTNER', order: 2 },
    { key: 'standard', name: '联合体成员', en: 'MEMBER', order: 3 },
];
/* ------------------------------------------------------------------ *
 * 八、守护服务：六大服务矩阵
 * ------------------------------------------------------------------ */
export const serviceCategories = [
    { key: 'hospital', name: '三甲医院绿色通道', icon: 'hospital', color: 'h', order: 1 },
    { key: 'mdt', name: 'MDT 多学科会诊', icon: 'mdt', color: 'e', order: 2 },
    { key: 'exam', name: '首保基地深度检测', icon: 'test', color: 'w', order: 3 },
    { key: 'spa', name: '定点疗养基地', icon: 'spa', color: 'h', order: 4 },
    { key: 'global', name: '跨境医疗与远程会诊', icon: 'global', color: 'e', order: 5 },
    { key: 'sos', name: '应急医疗保障', icon: 'sos', color: 'danger', order: 6 },
];
/* ------------------------------------------------------------------ *
 * 九、工单状态机（履约中台的核心，原型完全缺失）
 * ------------------------------------------------------------------ */
export const bookingStatuses = ['pending', 'matched', 'scheduled', 'serving', 'done', 'no_show', 'cancelled'];
/**
 * 中文名（**每套口径只有一份**：后台 `apps/admin/src/lib/format.ts` 直接引用本表，
 * 因此不存在「一个 key 两个中文名」的可能）。
 *
 * ⚠️ 新增 `no_show`（未到场）时**没有**再造 `accepted` / `in_progress` / `completed`：
 * 服务方工作台的四个动作与既有状态是**一对一**的 ——
 *   接单 → `matched`（已派单：由服务方承接）、开始履约 → `serving`、
 *   完成 → `done`、服务方取消 → `cancelled`。
 * 再造一组同义 key 只会得到「matched 与 accepted 都叫已接单」这种两个 key 一个
 * 中文名的局面，展示映射必然分叉。「成员未到场」在既有取值里确实没有对应项，
 * 它是**独立终态**（既不是已完成，也不是已取消），因此单独新增一个 key。
 */
export const bookingStatusLabels = {
    pending: '待受理',
    matched: '已派单',
    scheduled: '已预约',
    serving: '服务中',
    done: '已完成',
    no_show: '未到场',
    cancelled: '已取消',
};
/* ------------------------------------------------------------------ *
 * 十、号源状态（排期可约性的**唯一定义**）
 *
 * ⚠️ 这里同时回答两个问题，而且是**同一处**回答：
 *     ① 这个号源此刻能不能约（`slotStatusOf()` → 是否为 `open`）；
 *     ② 它对外该显示成什么状态、不可约时怎么向成员解释（`slotStatusLabels` / `slotStatusNotes`）。
 *
 * 为什么必须只有一处（这是本平台的真实缺陷）：「可约」判据曾在三处各算一遍 ——
 * 号源序列化（只看库状态与库存）、资源矩阵的可约数（SQL 里另写一份 `start_at > now`）、
 * 下单接口（再写一份时间判断）。三份判据里序列化那份**漏了时间条件**，于是已过期的
 * 号源对外是 `open`：成员在列表里看到「可预约」，一点下单必然 409。这与本仓库
 * 资源准入那段注释要避免的「列表显示可约、下单却失败」是同一类分叉，只是这次分叉的是时间判据。
 * 因此判据收敛到本文件：任何地方都不许再写第二份 `start_at > now`。
 * ------------------------------------------------------------------ */
export const slotStatuses = ['open', 'expired', 'full', 'closed'];
/**
 * 中文名（**每套口径只有一份**：C 端服务页与任何读号源状态的界面都引用本表，
 * 因此不存在「一个 key 两个中文名」的可能）。
 *
 * ⚠️ `expired` 是与 `full` / `closed` 并列的**第三种不可约原因**，不是其中任何一个的别名：
 *   已过期 = 开始时间已过（不可逆，只能改约其它时间）；
 *   已约满 = 名额满了（可能因他人取消而重新可约）；
 *   已停诊 = 机构关停了该时段（与个人无关，也不可恢复）。
 * 成员要据此决定「换个时间」还是「等一等」，所以三者必须各有各的名字与说明。
 */
export const slotStatusLabels = {
    open: '可预约',
    expired: '已过期',
    full: '已约满',
    closed: '已停诊',
};
/** 不可约原因的成员可读说明（对外文案的**唯一出处**，界面不另写一套说法）。 */
export const slotStatusNotes = {
    open: '',
    expired: '该时段已过开始时间，无法再预约，请选择其它时间。',
    full: '该时段名额已满，可等待他人取消释放名额，或选择其它时间。',
    closed: '该时段已被机构停诊，请选择其它时间。',
};
export function isSlotStatus(value) {
    return slotStatuses.includes(value);
}
/**
 * **号源状态判据（唯一出处）**：事实 + 当前时间 → 对外状态。
 *
 * 优先级：关停 → 过期 → 满号 → 可约。
 *   · `closed` 最先：机构关了这个时段，这与库存、时间都无关；
 *   · `expired` 先于 `full`：时间过去了是**不可逆**的。此时若告诉成员「已约满」，
 *     会让人以为等别人退号还能约上，而那个时段永远不会再来；
 *   · 库中出现未知 `status` 时按最严处理（并入 `closed`，即不可约），
 *     与 `resourceAdmission.ts`「未知状态按最严处理」同一口径，绝不当作可约。
 */
export function slotStatusOf(slot, now = new Date()) {
    if (slot.status === 'closed')
        return 'closed';
    const start = Date.parse(slot.startAt);
    const nowMs = typeof now === 'number' ? now : now.getTime();
    if (Number.isNaN(start) || start <= nowMs)
        return 'expired';
    if (slot.booked >= slot.capacity || slot.status === 'full')
        return 'full';
    return slot.status === 'open' ? 'open' : 'closed';
}
/**
 * 该号源此刻能否下单 = `slotStatusOf() === 'open'`。
 *
 * 之所以只做一层派生、不另写条件：列表页、可约数、下单校验都必须由**同一个状态**决定，
 * 否则「显示可约」与「真的能约」会再次分叉（这正是本节的由来）。
 */
export function isSlotBookable(slot, now = new Date()) {
    return slotStatusOf(slot, now) === 'open';
}
/* ------------------------------------------------------------------ *
 * 十一、合规文案（法务口径统一来源）
 * ------------------------------------------------------------------ */
export const complianceText = {
    threeNever: [
        { t: '绝不公开成员任何私人信息', d: '任何榜单、案例、宣传材料均不出现成员身份与个体数据' },
        { t: '绝不未经授权调用数据', d: '四级授权逐项独立开启，调用前校验、调用后留痕，可随时撤回' },
        { t: '绝不变更数据用途', d: '数据仅用于本人健康与家族治理服务，不用于任何商业转让' },
    ],
    boundary: '平台不直接开展临床诊疗，医疗服务由具备资质的合作医疗机构执行；金融、信托、法务类业务由具备相应牌照的合作机构落地实施。',
    disclaimer: 'AI 三螺旋管家仅提供辅助研判，不构成诊断或投资建议，最终方案由各领域人类专家确认落地。',
};
//# sourceMappingURL=domain.js.map